import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inputs, DEFAULT_INPUTS, IndustryType, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
type QuestionId = 'industry' | 'profitPerCustomer' | 'summerSales' | 'closeRate';
interface SliderQuestion {
  id: QuestionId;
  question: string;
  subtitle?: string;
  subtitleJSX?: React.ReactNode;
  field: keyof Inputs;
  type: 'range';
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
}
interface SelectQuestion {
  id: QuestionId;
  question: string;
  subtitle?: string;
  type: 'select';
  field: keyof Inputs;
  options: string[];
}
type Question = SliderQuestion | SelectQuestion;
const industryOptions = ['Windows', 'Roofing', 'Solar (Sales)', 'Solar (Vertical)', 'Solar (Community)', 'Pest', 'Fiber', 'Deregulated Energy', 'Hardscaping', 'Landscaping', 'Other'];
const questions: Question[] = [{
  id: 'industry',
  question: 'What industry are you in?',
  subtitle: 'This helps us provide industry-specific defaults',
  type: 'select',
  field: 'industry',
  options: industryOptions
}, {
  id: 'profitPerCustomer',
  question: 'What is your profit per customer?',
  subtitle: 'Gross profit per sale after commissions but before G&A / overhead',
  field: 'profitPerCustomer',
  type: 'range',
  min: 100,
  max: 50000,
  step: 100,
  formatValue: v => fmtMoney(v)
}, {
  id: 'summerSales',
  question: 'How many sales do you expect on average from a new rep who makes it through the summer?',
  subtitle: 'Total sales from a rep during the entire summer season (typically 4-6 months)',
  field: 'salesFirst90Total',
  type: 'range',
  min: 5,
  max: 50,
  step: 1,
  formatValue: v => `${v} sales`
}, {
  id: 'closeRate',
  question: 'What percentage of sales (signed contracts) do you expect to get installed?',
  subtitle: 'Your typical installation rate',
  field: 'pullthrough',
  type: 'range',
  min: 40,
  max: 100,
  step: 5,
  formatValue: v => `${v}%`
}];
export function CalculatorFlowSummer() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<Inputs>({
    ...DEFAULT_INPUTS,
    industry: 'Roofing',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const handleSliderChange = (field: keyof Inputs, value: number) => {
    const newInputs = {
      ...inputs,
      [field]: value
    };
    setInputs(newInputs);
    localStorage.setItem('calculatorInputsSummer', JSON.stringify(newInputs));
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    const newInputs = {
      ...inputs,
      industry: newIndustry,
      profitPerCustomer: defaults?.profitPerCustomer || inputs.profitPerCustomer,
      salesFirst90Total: defaults?.salesFirst90Total || inputs.salesFirst90Total,
      salesVetMonthly: defaults?.salesVetMonthly || inputs.salesVetMonthly
    };
    setInputs(newInputs);
    localStorage.setItem('calculatorInputsSummer', JSON.stringify(newInputs));
  };
  const handleContinue = () => {
    localStorage.setItem('calculatorInputsSummer', JSON.stringify(inputs));
    navigate('/retention-summer');
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-639">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-640">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-between" data-id="element-641">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-id="element-642" />
          <div className="px-3 py-1 bg-green-500/10 border border-green-500/30 rounded-full text-green-700 text-xs md:text-sm font-semibold" data-id="element-643">
            SUMMER PROGRAM
          </div>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 py-8 md:py-12" data-id="element-644">
        {/* Hero */}
        <div className="text-center mb-8 md:mb-12" data-id="element-645">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-646">
            Tell Us About Your Business
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-647">
            Answer these 4 quick questions to calculate your summer recruiting
            ROI
          </p>
        </div>

        {/* Questions Container */}
        <div className="space-y-6" data-id="element-648">
          {questions.map((question, index) => <div key={question.id} className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8" data-id="element-649">
              <div className="mb-6" data-id="element-650">
                <div className="flex items-start gap-3 mb-4" data-id="element-651">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm" data-id="element-652">
                    {index + 1}
                  </div>
                  <div className="flex-1" data-id="element-653">
                    <h2 className="text-lg md:text-xl font-bold text-navy mb-2" data-id="element-654">
                      {question.question}
                    </h2>
                    {question.type === 'range' && (question as SliderQuestion).subtitleJSX ? <p className="text-sm text-gray-600" data-id="element-655">
                        {(question as SliderQuestion).subtitleJSX}
                      </p> : question.subtitle ? <p className="text-sm text-gray-600" data-id="element-656">
                        {question.subtitle}
                      </p> : null}
                  </div>
                </div>
              </div>

              {/* Select Input for Industry */}
              {question.type === 'select' && <div data-id="element-657">
                  <select value={inputs[question.field] as string} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-4 rounded-lg border-2 border-gray-200 bg-white text-gray-900 text-lg font-semibold outline-none focus:border-green-500 transition-colors cursor-pointer" data-id="element-658">
                    <option value="" data-id="element-659">Select your industry</option>
                    {question.options.map(option => <option key={option} value={option} data-id="element-660">
                        {option}
                      </option>)}
                  </select>
                </div>}

              {/* Slider Input for Range Questions */}
              {question.type === 'range' && <div data-id="element-661">
                  <div className="flex items-center justify-between mb-3" data-id="element-662">
                    <span className="text-sm font-semibold text-gray-700" data-id="element-663">
                      Your Answer:
                    </span>
                    <span className="text-2xl md:text-3xl font-bold text-navy" data-id="element-664">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!(inputs[(question as SliderQuestion).field] as number) : inputs[(question as SliderQuestion).field]}
                    </span>
                  </div>
                  <input type="range" min={(question as SliderQuestion).min} max={(question as SliderQuestion).max} step={(question as SliderQuestion).step} value={inputs[(question as SliderQuestion).field] as number} onChange={e => handleSliderChange((question as SliderQuestion).field, parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500" style={{
              background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((inputs[(question as SliderQuestion).field] as number) - ((question as SliderQuestion).min || 0)) / (((question as SliderQuestion).max || 100) - ((question as SliderQuestion).min || 0)) * 100}%, #E5E7EB ${((inputs[(question as SliderQuestion).field] as number) - ((question as SliderQuestion).min || 0)) / (((question as SliderQuestion).max || 100) - ((question as SliderQuestion).min || 0)) * 100}%, #E5E7EB 100%)`
            }} data-id="element-665" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-666">
                    <span data-id="element-667">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!((question as SliderQuestion).min || 0) : (question as SliderQuestion).min}
                    </span>
                    <span data-id="element-668">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!((question as SliderQuestion).max || 100) : (question as SliderQuestion).max}
                    </span>
                  </div>
                </div>}
            </div>)}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center" data-id="element-669">
          <button onClick={handleContinue} className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg transition-all shadow-xl hover:shadow-green-500/50 hover:scale-105 active:scale-100" data-id="element-670">
            <span data-id="element-671">Continue to Recruiting Funnel</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-672">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-673" />
            </svg>
          </button>
        </div>
      </div>
    </div>;
}